import { DatePicker, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import CaretDownIcon from "../../../../assets/icons/CaretDownIcon";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetMyDepartmentTicketStatesQuery } from "../../../../features/superAdmin/dashboard/dashboardApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import { MyDepartmentState } from "../../../../types/dashboard";
import { addCommaInNumber } from "../../../../utils/number";
import { DFilters } from "../Dashboard";

const { RangePicker } = DatePicker;
type RangeValue<T> = [EventValue<T>, EventValue<T>] | null;
type EventValue<T> = T | null;

export default function MyDeptTicketStates({
  toDate,
  fromDate,
  limit: l,
  skip: p,
  ...filters
}: DFilters) {
  const { filter, handleFilter, page, limit, handleLimit, handlePagination } =
    useTable<DFilters>({
      defaultLimit: 5,
    });

  const { data, isLoading, isFetching, isError, error } =
    useGetMyDepartmentTicketStatesQuery({
      params: {
        ...filters,
        page,
        limit,
        fromDate: filter?.fromDate || fromDate || undefined,
        toDate: filter?.toDate || toDate || undefined,
      },
    });

  useError(isError, error, "Top performers get fail");

  useEffect(() => {
    if (toDate && fromDate) {
      // Reset filters
      handleFilter("fromDate", undefined);
      handleFilter("toDate", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toDate, fromDate]);

  const columns: ColumnsType<MyDepartmentState> = [
    {
      title: "Date",
      dataIndex: "date",
      align: "center",
      render: (val) => dayjs(val).format("DD MMM, YYYY"),
    },
    {
      title: "Total Ticket",
      dataIndex: "total",
      align: "center",
      render: (val) => addCommaInNumber(Number(val || 0)),
    },
    {
      title: "In Progress",
      dataIndex: "in_progress",
      align: "center",
      render: (val) => addCommaInNumber(Number(val || 0)),
    },
    {
      title: "Resolved",
      dataIndex: "resolved",
      align: "center",
      render: (val) => addCommaInNumber(Number(val || 0)),
    },
    {
      title: "Closed",
      dataIndex: "closed",
      align: "center",
      render: (val) => addCommaInNumber(Number(val || 0)),
    },
    {
      title: "SLA Breached",
      dataIndex: "sla_breached",
      align: "center",
      render: (val) => addCommaInNumber(Number(val || 0)),
    },
    {
      title: "Within SLA",
      dataIndex: "within_sla",
      align: "center",
      render: (val) => addCommaInNumber(Number(val || 0)),
    },
  ];

  return (
    <div className="p-5 rounded-xl bg-white shadow">
      <h2 className="text-xl text-header font-medium">
        Tickets of my department
      </h2>
      <CustomTable
        className="designationTable"
        limit={limit}
        page={page}
        columns={columns}
        dataSource={data?.data?.data}
        loading={isLoading || isFetching}
        handlePagination={handlePagination}
        total={data?.data?.totalLength}
        topSectionClassName="w-full px-0"
        rootClassName="border-none"
        filterSection={
          <div className="flex justify-between items-center w-full">
            <div className="flex gap-2 items-center">
              <div>Total Rows</div>
              <Select
                placeholder="5"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                style={{ width: 70 }}
                onChange={handleLimit}
                value={limit}
                options={[
                  { label: "5", value: 5 },
                  { label: "10", value: 10 },
                  { label: "20", value: 20 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 },
                  { label: "1000", value: 1000 },
                ]}
              />
              <div>of {data?.data?.totalLength}</div>
            </div>
            <RangePicker
              className="max-w-64 !h-10"
              value={
                filter.fromDate && filter.toDate
                  ? [dayjs(filter.fromDate), dayjs(filter.toDate)]
                  : null
              }
              onChange={(dates: RangeValue<Dayjs>) => {
                if (dates && dates[0] && dates[1]) {
                  handleFilter("fromDate", dates[0].format("YYYY-MM-DD"));
                  handleFilter("toDate", dates[1].format("YYYY-MM-DD"));
                } else {
                  handleFilter("fromDate", undefined);
                  handleFilter("toDate", undefined);
                }
              }}
            />
          </div>
        }
      />
    </div>
  );
}
